/**
 * Surface data model. Every drivable cell on a track references one of these
 * definitions; the vehicle physics samples the surface under each wheel and
 * blends the parameters, which is what makes half-on-the-kerb handling work.
 */

export type SurfaceId =
  | 'asphalt'
  | 'wet'
  | 'gravel'
  | 'grass'
  | 'dirt'
  | 'sand'
  | 'mud'
  | 'ice'
  | 'kerb'
  | 'boost'
  | 'oil'
  | 'wall';

export type SurfaceSound = 'tarmac' | 'gravel' | 'dirt' | 'grass' | 'wet' | 'ice' | 'sand';

export interface SurfaceDef {
  id: SurfaceId;
  name: string;
  /** Multiplier applied to the tire grip coefficient. */
  grip: number;
  /** Multiplier applied to rolling resistance. */
  rollResist: number;
  /** Multiplier applied to aerodynamic drag (loose surfaces drag harder). */
  drag: number;
  /** Multiplier applied to engine force (sand/grass bog a car down). */
  accel: number;
  /** Multiplier applied to top speed. */
  topSpeed: number;
  /** How strongly this surface darkens tire marks / rubber (0 = none). */
  skid: number;
  /** Particle emission strength 0..1. */
  particle: number;
  /** Particle + dust colour as a CSS hex string. */
  particleColor: string;
  /** Whether the surface renders tire marks on the track. */
  marks: boolean;
  /** Tint used when rasterising the physics grid for debugging. */
  debugColor: string;
  /** Rolling noise flavour for the audio engine. */
  sound: SurfaceSound;
  /** Some surfaces silently add speed (boost pads, ice slides). */
  boost?: number;
  /** Non-solid surfaces can be driven on; walls cannot. */
  solid: boolean;
}

export const SURFACES: Record<SurfaceId, SurfaceDef> = {
  asphalt: {
    id: 'asphalt', name: 'Asphalt', grip: 1, rollResist: 1, drag: 1, accel: 1, topSpeed: 1,
    skid: 1, particle: 0.25, particleColor: '#9aa3ad', marks: true, debugColor: '#3a3a44',
    sound: 'tarmac', solid: false,
  },
  wet: {
    id: 'wet', name: 'Wet Asphalt', grip: 0.7, rollResist: 1, drag: 1, accel: 0.96, topSpeed: 0.97,
    skid: 0.8, particle: 0.6, particleColor: '#cfe4ff', marks: true, debugColor: '#2f4a63',
    sound: 'wet', solid: false,
  },
  gravel: {
    id: 'gravel', name: 'Gravel', grip: 0.62, rollResist: 2.1, drag: 1.25, accel: 0.86, topSpeed: 0.86,
    skid: 0.4, particle: 0.9, particleColor: '#b8ab95', marks: true, debugColor: '#7d7466',
    sound: 'gravel', solid: false,
  },
  grass: {
    id: 'grass', name: 'Grass', grip: 0.5, rollResist: 3.0, drag: 1.5, accel: 0.7, topSpeed: 0.68,
    skid: 0.25, particle: 0.7, particleColor: '#4f8a3a', marks: true, debugColor: '#2f5a25',
    sound: 'grass', solid: false,
  },
  dirt: {
    id: 'dirt', name: 'Dirt', grip: 0.72, rollResist: 2.2, drag: 1.2, accel: 0.87, topSpeed: 0.88,
    skid: 0.4, particle: 0.85, particleColor: '#9c7b52', marks: true, debugColor: '#6b5436',
    sound: 'dirt', solid: false,
  },
  sand: {
    id: 'sand', name: 'Sand', grip: 0.58, rollResist: 3.4, drag: 1.6, accel: 0.62, topSpeed: 0.66,
    skid: 0.3, particle: 0.9, particleColor: '#e0cd94', marks: true, debugColor: '#b8a26e',
    sound: 'sand', solid: false,
  },
  mud: {
    id: 'mud', name: 'Mud', grip: 0.42, rollResist: 3.8, drag: 1.8, accel: 0.6, topSpeed: 0.6,
    skid: 0.35, particle: 0.8, particleColor: '#5b4630', marks: true, debugColor: '#4a3a28',
    sound: 'dirt', solid: false,
  },
  ice: {
    id: 'ice', name: 'Ice', grip: 0.16, rollResist: 0.7, drag: 0.9, accel: 0.8, topSpeed: 0.95,
    skid: 0.15, particle: 0.3, particleColor: '#eaf6ff', marks: false, debugColor: '#cfe6f5',
    sound: 'ice', solid: false,
  },
  kerb: {
    id: 'kerb', name: 'Kerb', grip: 0.86, rollResist: 1.35, drag: 1.05, accel: 0.95, topSpeed: 0.96,
    skid: 0.6, particle: 0.5, particleColor: '#c9ccd2', marks: true, debugColor: '#b03030',
    sound: 'tarmac', solid: false,
  },
  boost: {
    id: 'boost', name: 'Boost Pad', grip: 1.05, rollResist: 0.8, drag: 0.9, accel: 1.2, topSpeed: 1.05,
    skid: 0.2, particle: 0.4, particleColor: '#5ce1ff', marks: false, debugColor: '#2ac6e6',
    sound: 'tarmac', boost: 1, solid: false,
  },
  oil: {
    id: 'oil', name: 'Oil Slick', grip: 0.14, rollResist: 0.9, drag: 1, accel: 0.95, topSpeed: 1,
    skid: 0.1, particle: 0.35, particleColor: '#2a2a30', marks: false, debugColor: '#141418',
    sound: 'wet', solid: false,
  },
  wall: {
    id: 'wall', name: 'Barrier', grip: 1, rollResist: 1, drag: 1, accel: 0, topSpeed: 0,
    skid: 0, particle: 0.9, particleColor: '#ffd479', marks: false, debugColor: '#101014',
    sound: 'tarmac', solid: true,
  },
};

export const SURFACE_LIST: SurfaceId[] = Object.keys(SURFACES) as SurfaceId[];
export const SURFACE_INDEX: Record<SurfaceId, number> = SURFACE_LIST.reduce(
  (acc, id, i) => {
    acc[id] = i;
    return acc;
  },
  {} as Record<SurfaceId, number>,
);

export const surfaceById = (index: number): SurfaceDef => SURFACES[SURFACE_LIST[index] ?? 'asphalt'];
